import React, { useRef, useEffect, useCallback } from 'react';
import { Card, Avatar, List, Tag, Space, Typography, Divider, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { AudioMutedOutlined, AudioOutlined } from '@ant-design/icons';
import { setLocalMute } from '../../store/actions/InterviewActions';
import * as actions from '../../store/actions';
import { setLocalVideoElement, setRemoteMute } from '../../store/actions/index';
import styles from './PerticepentsVideoContainer.scss';

import { VideoOff } from 'lucide-react';

const { Text } = Typography;

const ParticipantAvatar = ({ participant, attendees }) => {
  const attendeesList = Object.values(attendees);

  const isLocalAttendee = attendeesList.find(att => {
    const tile = att?.tileState;
    return (
      (participant?.id === tile?.boundExternalUserId || tile?.boundExternalUserId === undefined) &&
      tile?.localTile &&
      !tile?.isContent
    );
  });

  const isRemoteAttendee = attendeesList.find(att => {
    const tile = att?.tileState;
    return participant?.id === tile?.boundExternalUserId?.split('.')[0] && !tile?.localTile;
  });

  console.log('Is local attendee:', isLocalAttendee);
  console.log('Is remote attendee:', isRemoteAttendee);

  let statusColor = '#ff4d4f';

  if (isLocalAttendee && !isRemoteAttendee) {
    statusColor = '#52c41a';
  } else if (isRemoteAttendee && !isRemoteAttendee?.hasLeft) {
    statusColor = '#52c41a';
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Avatar
        size="small"
        className={styles.participantsAvatar}
      >
        {participant.name[0]}
      </Avatar>
      {/* Status indicator dot */}
      <div
        style={{
          position: 'absolute',
          top: -3,
          right: -3,
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: statusColor,
          border: '2px solid white',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  );
};

const VideoTile = ({
  tileState,
  audioVideo,
  dispatch,
  isRemoteVideoOn = false,
  isLocal = false,
}) => {
  const videoRef = useRef(null);

  const setVideoRef = useCallback(
    el => {
      videoRef.current = el;
      if (el && isLocal) {
        dispatch(setLocalVideoElement(el));
      }
    },
    [isLocal, dispatch],
  );

  useEffect(() => {
    if (!tileState || !videoRef.current || !audioVideo) return;

    if (tileState.tileId) {
      audioVideo.bindVideoElement(tileState.tileId, videoRef.current);
    }
  }, [tileState, audioVideo]);

  if (!tileState) return null;

  return isLocal ? (
    <>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundColor: tileState.active ? 'transparent' : '#000',
          borderRadius: '10px',
        }}
      >
        <video
          ref={setVideoRef}
          className={styles.videoElement}
          muted={isLocal}
          playsInline
          autoPlay
          style={{
            display: tileState.active ? 'block' : 'none',
            width: '100%',
            height: '100%',
          }}
          data-testid="local-video-active"
        />
        {!tileState.active && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#fff',
            }}
            data-testid="local-video-inactive"
          >
            <VideoOff size={48} />
          </div>
        )}
      </div>
    </>
  ) : (
    <>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <video
          ref={setVideoRef}
          className={styles.videoElement}
          muted={isLocal}
          playsInline
          autoPlay
          style={{
            display: isRemoteVideoOn && 'block',
            width: '100%',
            height: '100%',
          }}
          data-testid="remote-video-active"
        />
        {!isRemoteVideoOn && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#fff',
            }}
            data-testid="remote-video-inactive"
          >
            <VideoOff size={48} />
          </div>
        )}
      </div>
    </>
  );
};

const VideoCallUI = () => {
  const dispatch = useDispatch();
  const participants = useSelector(state => state.interview.participants || []);
  const currentUserEmail = localStorage.getItem('username');

  const { meetingSession, videoTiles, localTileId, localMute, remoteMuteStatus, attendees } =
    useSelector(state => state.interviewRecording);
  const { id } = useSelector(state => state.meeting);

  useEffect(() => {
    if (!meetingSession || !participants.length) return;

    const subscribeToRemoteMutes = async () => {
      for (const participant of participants) {
        console.log('Subscribing to participant:', participant);
        console.log('Current user email:', currentUserEmail);
        if (currentUserEmail !== participant.email) {
          try {
            const fetchRemoteAttendee = await dispatch(actions.fetchAttendee(id, participant.id));
            const handleRemoteMute = (attendeeId, volume, muted, signalStrength) => {
              const remoteAttendees = Array.isArray(fetchRemoteAttendee)
                ? fetchRemoteAttendee
                : [fetchRemoteAttendee];

              remoteAttendees.forEach(att => {
                const prevMuted = remoteMuteStatus?.[att.participant_id];
                if (muted !== null && prevMuted !== muted) {
                  console.log('is the mute status changes');
                  dispatch(setRemoteMute(att.participant_id, muted));
                }
              });
            };

            meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
              fetchRemoteAttendee.aws_attendee_id,
              handleRemoteMute,
            );
            console.log('Subscribed to mute for', participant.id);
          } catch (error) {
            console.error('Error subscribing to remote mute:', error);
          }
        }
      }
    };

    subscribeToRemoteMutes();
  }, [participants, id, meetingSession, dispatch, currentUserEmail]);

  const sessionAudioVideo = meetingSession?.audioVideo;

  const toggleMute = () => {
    if (!sessionAudioVideo) return;

    const newMuteState = !localMute;
    dispatch(setLocalMute(newMuteState));

    if (newMuteState) {
      sessionAudioVideo.realtimeMuteLocalAudio();
    } else {
      sessionAudioVideo.realtimeUnmuteLocalAudio();
    }
  };

  const remoteTiles = Object.values(attendees).filter(
    attendee =>
      attendee?.tileState &&
      !attendee?.hasLeft &&
      !attendee?.tileState?.localTile &&
      !attendee?.tileState.isContent,
  );

  return (
    <div className={styles.videoCallContainer}>
      <div className={styles.videoCallHeader}>Video Call</div>
      <Divider className={styles.videoCallDivider} />

      <div className={styles.videoContainer}>
        {localTileId && (
          <Card
            size="small"
            bodyStyle={{ padding: 0 }}
            className={styles.videoCard}
          >
            <div className={styles.videoWrapper}>
              <VideoTile
                tileState={videoTiles.get(localTileId)}
                isLocal={true}
                audioVideo={sessionAudioVideo}
                dispatch={dispatch}
              />
              <div className={styles.participantLabel}>You</div>
            </div>
          </Card>
        )}

        {remoteTiles.map(attendee => {
          const externalId = attendee.tileState.boundExternalUserId.split('.')[0];
          const remoteParticipant = participants.find(p => p.id === externalId);
          const label =
            remoteParticipant?.role === 'CA'
              ? 'Candidate'
              : remoteParticipant?.role === 'IN'
              ? 'Interviewer'
              : '';
          return (
            <Card
              key={attendee.tileId}
              size="small"
              bodyStyle={{ padding: 0 }}
              className={styles.videoCard}
            >
              <div className={styles.videoWrapper}>
                <VideoTile
                  tileState={attendee.tileState}
                  isRemoteVideoOn={attendee.isVideoOn}
                  audioVideo={sessionAudioVideo}
                  dispatch={dispatch}
                />
                <div className={styles.participantLabel}>{label}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Divider className={styles.participantsDivider} />

      <div className={styles.participantsSection}>
        <Text
          strong
          className={styles.participantsTitle}
        >
          Participants ({participants.length})
        </Text>

        <List
          size="small"
          itemLayout="horizontal"
          className={styles.participantsList}
          dataSource={participants}
          renderItem={item => (
            <List.Item className={styles.participantsListItem}>
              <List.Item.Meta
                avatar={
                  <ParticipantAvatar
                    participant={item}
                    attendees={attendees}
                  />
                }
                title={
                  <Space size="small">
                    <Text style={{ fontSize: 13 }}>{item.name}</Text>
                    {item.email === currentUserEmail ? (
                      <Button
                        type="text"
                        size="small"
                        className={styles.muteButton}
                        onClick={toggleMute}
                        icon={
                          localMute ? (
                            <AudioMutedOutlined
                              className={`${styles.audioIcon} ${styles.muted}`}
                              data-testid={'local-mute'}
                            />
                          ) : (
                            <AudioOutlined
                              className={`${styles.audioIcon} ${styles.unmuted}`}
                              data-testid={'local-unmute'}
                            />
                          )
                        }
                      />
                    ) : remoteMuteStatus[item.id] ? (
                      <AudioMutedOutlined
                        className={`${styles.audioIcon} ${styles.muted}`}
                        data-testid={'remote-mute'}
                      />
                    ) : (
                      <AudioOutlined
                        className={`${styles.audioIcon} ${styles.unmuted}`}
                        data-testid={'remote-unmute'}
                      />
                    )}
                    {item.email === currentUserEmail && <Tag color="green">You</Tag>}
                    {item.role === 'IN' && <Tag color="blue">Interviewer</Tag>}
                    {item.role === 'CA' && <Tag color="orange">Candidate</Tag>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </div>
  );
};

export default VideoCallUI;
